import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  DECLINE_REASON_OTHER,
  PROFILE_UPDATE_DECLINE_REASONS,
} from "../utils/decline-reasons";

const REASON_MAX_LENGTH = 500;

interface Props {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  userName: string;
  isSubmitting: boolean;
  onSubmit: (reason: string) => void;
  // Optional override; defaults to the PROFILE_UPDATE list for backward compat.
  reasons?: readonly string[];
  title?: string;
}

export const DeclineReasonDialog = ({
  isOpen,
  onOpenChange,
  userName,
  isSubmitting,
  onSubmit,
  reasons = PROFILE_UPDATE_DECLINE_REASONS,
  title,
}: Props) => {
  const [selected, setSelected] = useState<string>("");
  const [freeText, setFreeText] = useState<string>("");

  // Reset on close so a re-open starts fresh.
  useEffect(() => {
    if (!isOpen) {
      setSelected("");
      setFreeText("");
    }
  }, [isOpen]);

  const isOther = selected === DECLINE_REASON_OTHER;
  const finalReason = isOther ? freeText.trim() : selected;
  const overLimit = finalReason.length > REASON_MAX_LENGTH;
  const canSubmit =
    !!selected && finalReason.length > 0 && !overLimit && !isSubmitting;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {title ?? `Decline profile update for ${userName}`}
          </DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Reason</label>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger>
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {reasons.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isOther && (
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Please specify a reason
              </label>
              <Textarea
                placeholder="Type your reason here"
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                className="min-h-[100px]"
                maxLength={REASON_MAX_LENGTH + 1}
              />
              <div
                className={`text-xs text-right ${
                  overLimit ? "text-red-600" : "text-gray-500"
                }`}
              >
                {finalReason.length} / {REASON_MAX_LENGTH}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!canSubmit}
            onClick={() => onSubmit(finalReason)}
          >
            {isSubmitting ? "Declining…" : "Decline"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeclineReasonDialog;
